import React, { useCallback, useEffect, useState } from 'react';
import _fetch from 'utils/fetch';
import { withAlerts, AlertsState } from 'context/alerts';
import { credentialIsNotExpired, parseJwt } from './jwt-parser';
import { useLocation, useNavigate } from 'react-router-dom';

export interface UserProfile {
  name: string;
  email: string;
  picture: string;
}

export type AuthState = {
  loggedIn: boolean | undefined;
  sandboxesAvailable: Sandbox[];
  sandboxId: string | undefined;
  sandbox: Sandbox | undefined;
  acceptSandboxTerms(): void;
  securityExamValidUntil: Date;
  securityExamRequired: boolean;
  trainingSiteURL: string;
  google: any;
  userProfile: UserProfile;
  credential: string;
  getCredentialOrPromptLogin(): string;
  logout(email: string): void;
};

const AuthContext = React.createContext<AuthState>({
  loggedIn: undefined,
  sandboxesAvailable: [],
  acceptSandboxTerms: () => null,
  securityExamValidUntil: new Date(1, 1, 1),
  securityExamRequired: false,
  trainingSiteURL: '',
  sandboxId: undefined,
  sandbox: undefined,
  google: null as any,
  userProfile: { name: '', email: '', picture: '' },
  credential: '',
  getCredentialOrPromptLogin: () => '',
  logout: () => null,
});

interface Props {
  alerts: AlertsState;
  auth: AuthState;
}

const provideAuth = (WrappedComponent: any) => {
  const ProvideAuth = (props: Props): JSX.Element | null => {
    const [loggedIn, setLoggedIn] = useState<boolean | undefined>();
    const [sandboxesAvailable, setSandboxesAvailable] = useState<Sandbox[]>([]);
    const [sandbox, setSandbox] = useState<Sandbox | undefined>();
    const [securityExamValidUntil, setSecurityExamValidUntil] = useState(
      new Date(1, 1, 1)
    );
    const [securityExamRequired, setSecurityExamRequired] = useState(false);
    const [trainingSiteURL, setTrainingSiteURL] = useState('');
    const [google, setGoogle] = useState<any>(null);
    const [userProfile, setUserProfile] = useState<UserProfile>({
      name: '',
      email: '',
      picture: '',
    });
    const [credential, setCredential] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const pathParts = location.pathname.split('/');
    const sandboxId = pathParts.length > 1 ? pathParts[1] : '';

    const handlePromptMomentNotification = (notification: any) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // Automatical sign-in canceled / failed
        setLoggedIn(false);
      }
    };

    const getCredentialOrPromptLogin = () => {
      if (credentialIsNotExpired(credential)) {
        return credential;
      }
      setLoggedIn(undefined);
      google.accounts.id.prompt(handlePromptMomentNotification);
      return '';
    };

    const logout = (email: string) => {
      setLoggedIn(false);
      setSandbox(undefined);
      setSandboxesAvailable([]);
      (window as any).google?.accounts.id.revoke(email);
    };

    const updateSandboxSelection = useCallback(
      (availableSandboxes: Sandbox[]) => {
        if (sandbox && sandboxId === '') {
          // we navigated away from sandboxes
          setSandbox(undefined);
          return;
        }

        const newSandbox = availableSandboxes.find(
          sandbox => sandbox.id === sandboxId
        );
        setSandbox(newSandbox);
      },
      [sandboxId]
    );

    useEffect(() => {
      const fetchSecurityExamStatus = async (token: string) => {
        const url = `${process.env.REACT_APP_API_HOST}/user/security-exam`;
        try {
          const json = await _fetch({
            url,
            options: {
              method: 'get',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            },
            responseReturnType: 'json',
          });
          return json;
        } catch (err: any) {
          props.alerts.setMessage(err, 'danger');
          return null;
        }
      };

      const fetchSandboxes = async (
        token: string
      ): Promise<Sandbox[] | null> => {
        const url = `${process.env.REACT_APP_API_HOST}/user/list-sandboxes`;
        try {
          const json = await _fetch({
            url,
            options: {
              method: 'get',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            },
            responseReturnType: 'json',
          });
          const sandboxes = json.map((sandbox: any) => ({
            id: sandbox.id,
            name: sandbox.name,
            termsAccepted: sandbox.terms_accepted,
            themeInformation: sandbox.theme_information,
          }));
          return sandboxes;
        } catch (err: any) {
          props.alerts.setMessage(err, 'danger');
          return null;
        }
      };

      const handleCredentialResponse = async (credentialResponse: any) => {
        const credential = credentialResponse.credential;
        setCredential(credential);
        const parsed = parseJwt(credentialResponse.credential);
        setUserProfile({
          name: parsed.name,
          email: parsed.email,
          picture: parsed.picture,
        });

        // if there was an previous error message on display from previous logon attempt clear it first
        props.alerts.clearMessage();
        setLoggedIn(undefined);
        const securityInfo = await fetchSecurityExamStatus(credential);
        if (securityInfo === null) {
          logout(parsed.email);
          return;
        }
        setTrainingSiteURL(securityInfo.training_site_url);
        setSecurityExamRequired(securityInfo.required);

        const securityExamValidUntil = new Date(
          Date.parse(securityInfo.valid_until)
        );
        setSecurityExamValidUntil(securityExamValidUntil);

        if (securityInfo.required && new Date() > securityExamValidUntil) {
          navigate('/examnotvalid');
          return;
        }

        const availableSandboxes = await fetchSandboxes(credential);
        if (availableSandboxes === null) {
          logout(parsed.email);
          return;
        }

        setSandboxesAvailable(availableSandboxes);
        updateSandboxSelection(availableSandboxes);
        setLoggedIn(true);
      };

      const initializeGoogle = () => {
        try {
          const google = (window as any).google;
          if (!google) {
            return;
          }
          setGoogle(google);
          google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENTID,
            // Automatical sign-in, does not succeed when user has previously logged in with multiple accounts
            auto_select: true,
            ux_mode: 'popup',
            callback: handleCredentialResponse,
          });
          // Prompt immediately to do automatical sign-in
          google.accounts.id.prompt(handlePromptMomentNotification);
        } catch (err: any) {
          props.alerts.setMessage(
            {
              error: `Sorry, but we're having trouble signing you in.
                    Please make sure that you have enabled third party cookies and try again by reloading the page.
                    If this doesn't work, you might want to contact your administrator.`,
            },
            'danger',
            false,
            false
          );
          navigate('/');
        }
      };

      let scriptElement = document.createElement('script');
      scriptElement.src = 'https://accounts.google.com/gsi/client';
      scriptElement.async = true;
      scriptElement.onload = initializeGoogle;
      document.head.append(scriptElement);
    }, []);

    useEffect(() => {
      // Path change indicates possible sandbox selection
      if (loggedIn) {
        updateSandboxSelection(sandboxesAvailable);
      }
    }, [sandboxesAvailable, loggedIn, updateSandboxSelection]);

    /**
     * When user accepts terms in sandbox page we need to update them manually here.
     * This is because we don't refresh the page so the auth state won't update.
     */
    const acceptSandboxTerms = (): void => {
      const { id, name, themeInformation } = sandbox || {
        id: '',
        name: '',
        themeInformation: {},
      };
      const newSandbox = {
        id,
        name,
        termsAccepted: true,
        themeInformation,
      };

      setSandbox(newSandbox);
      setSandboxesAvailable(prevSandboxesAvailable => {
        let sandboxes = [...prevSandboxesAvailable];
        const updatedSandbox = sandboxes.find(sandbox => sandbox.id === id);
        if (updatedSandbox !== undefined) {
          updatedSandbox.termsAccepted = true;
        }
        return sandboxes;
      });
    };

    return (
      <AuthContext.Provider
        value={{
          loggedIn: loggedIn,
          sandboxesAvailable: sandboxesAvailable,
          sandboxId: sandboxId,
          sandbox: sandbox,
          acceptSandboxTerms: acceptSandboxTerms,
          securityExamValidUntil: securityExamValidUntil,
          securityExamRequired: securityExamRequired,
          trainingSiteURL: trainingSiteURL,
          google: google,
          userProfile: userProfile,
          credential: credential,
          getCredentialOrPromptLogin: getCredentialOrPromptLogin,
          logout: logout,
        }}
      >
        <WrappedComponent {...props} />
      </AuthContext.Provider>
    );
  };
  return withAlerts(ProvideAuth);
};

// eslint-disable-next-line react/display-name
const withAuth = (WrappedComponent: any) => (props: any) =>
  (
    <AuthContext.Consumer>
      {(v: AuthState) => <WrappedComponent {...props} auth={v} />}
    </AuthContext.Consumer>
  );

export default AuthContext;
export { provideAuth, withAuth };
